import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthProvider } from './lib/auth/AuthProvider';
import { ProtectedRoute } from './lib/auth/ProtectedRoute';
import { isDataUploadsEnabled, isWellsEnabled } from './lib/features';
import { DashboardLayout } from './components/layouts/DashboardLayout';
import { AuthCallback } from './pages/AuthCallback';
import { Forbidden } from './pages/Forbidden';
import { Dashboard } from './pages/Dashboard';
import { IssuesListPage } from './pages/IssuesListPage';
import { IssueSubmitPage } from './pages/IssueSubmitPage';
import { IssueDetailPage } from './pages/IssueDetailPage';
import { UploadsListPage } from './pages/UploadsListPage';
import { UploadNewPage } from './pages/UploadNewPage';
import { ReleasesListPage } from './pages/ReleasesListPage';
import { ReleaseDetailPage } from './pages/ReleaseDetailPage';
import { WellsPage } from './pages/WellsPage';
import { CustomersList } from './pages/admin/CustomersList';
import { CustomerCreate } from './pages/admin/CustomerCreate';
import { CustomerDetail } from './pages/admin/CustomerDetail';
import { AuditLog } from './pages/admin/AuditLog';
import { Toaster } from './components/ui/sonner';

// /bugs/:id → /issues/:id — forward the id param through the redirect so
// bookmarks and mid-flight links still land on the right issue.
function LegacyBugIdRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/issues/${id ?? ''}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/403" element={<Forbidden />} />

          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/issues" element={<IssuesListPage />} />
            <Route path="/issues/new" element={<IssueSubmitPage />} />
            <Route path="/issues/:id" element={<IssueDetailPage />} />
            {/* Legacy /bugs routes — kept as redirects so existing links and
                bookmarks keep working after the Phase 5 chunk-3 rename. */}
            <Route path="/bugs" element={<Navigate to="/issues" replace />} />
            <Route path="/bugs/new" element={<Navigate to="/issues/new" replace />} />
            <Route path="/bugs/:id" element={<LegacyBugIdRedirect />} />
            {isDataUploadsEnabled() ? (
              <>
                <Route path="/uploads" element={<UploadsListPage />} />
                <Route path="/uploads/new" element={<UploadNewPage />} />
              </>
            ) : (
              <>
                {/* PLAN 5-20: the standalone data-submission surface is
                    feature-flagged off in Phase 5 and reassigned to the
                    Acoustic phase. Redirect any stale link to the dashboard
                    rather than 404 so an admin who bookmarked /uploads
                    lands somewhere sensible. */}
                <Route path="/uploads" element={<Navigate to="/dashboard" replace />} />
                <Route path="/uploads/new" element={<Navigate to="/dashboard" replace />} />
              </>
            )}
            <Route path="/releases" element={<ReleasesListPage />} />
            <Route path="/releases/:repoName" element={<ReleaseDetailPage />} />
            {/* Wells telemetry (Phase 6). Held back from the initial launch
                behind VITE_FEATURE_WELLS; when the nav item is hidden the route
                redirects rather than 404s, matching the /uploads treatment
                above, so a stale bookmark lands on the dashboard. */}
            {isWellsEnabled() ? (
              <Route path="/wells" element={<WellsPage />} />
            ) : (
              <Route path="/wells" element={<Navigate to="/dashboard" replace />} />
            )}
          </Route>

          <Route
            element={
              <ProtectedRoute requiredRole="admin">
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin/customers" element={<CustomersList />} />
            <Route path="/admin/customers/new" element={<CustomerCreate />} />
            <Route path="/admin/customers/:id" element={<CustomerDetail />} />
            <Route path="/admin/audit-log" element={<AuditLog />} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}
